import {
	ARCADE_LIST,
	type ArcadeId,
	type BestOf,
	CATEGORY_LABEL,
	GAME_CATEGORIES,
	GAME_LIST,
	type GameCategory,
	type GameId,
	type TurnSeconds,
} from "@playpal/core";
import { useState } from "react";
import { type FavouriteKey, favouriteKey, favourites, toggleFavourite } from "../lib/favourites";
import { surpriseGame } from "../lib/history";
import { play } from "../lib/sound";
import { MatchSettings } from "./RoomExtras";
import { Button, Card } from "./ui";

/**
 * One shelf entry, whether it is a turn game or an arcade race.
 *
 * The two are different enough underneath to keep their registries apart, but a
 * child looking for something to play does not care which kind a thing is —
 * they care that it is on the shelf they are looking at and that searching for
 * "snake" finds it.
 */
type Shelved =
	| { kind: "game"; id: GameId; title: string; emoji: string; blurb: string }
	| { kind: "arcade"; id: ArcadeId; title: string; emoji: string; blurb: string };

const ARCADE_CATEGORY = "arcade";

interface Props {
	onPick: (gameId: GameId, options: { bestOf: BestOf; turnSeconds: TurnSeconds }) => void;
	onArcade: (arcadeId: ArcadeId) => void;
}

export function GamePicker({ onPick, onArcade }: Props) {
	const [bestOf, setBestOf] = useState<BestOf>(1);
	/*
	 * A clock, on, and the most generous one there is.
	 *
	 * With no clock a game has no way to end except somebody playing it. Two
	 * children in the same room sort that out by talking; two children in
	 * different houses cannot, and the walk-away forfeit only covers a player
	 * whose connection has actually dropped — not one who has wandered off with
	 * the tab still open. Forty-five seconds is long enough to think properly
	 * about a move and short enough that nobody is stuck all evening, and it is
	 * two taps to turn off.
	 */
	const [turnSeconds, setTurnSeconds] = useState<TurnSeconds>(45);
	const [category, setCategory] = useState<GameCategory | "all" | typeof ARCADE_CATEGORY>("all");
	const [search, setSearch] = useState("");
	const [starred, setStarred] = useState<readonly FavouriteKey[]>(favourites);

	// Only show shelves that actually have something on them.
	const stocked = GAME_CATEGORIES.filter((name) =>
		GAME_LIST.some((game) => game.category === name),
	);

	const needle = search.trim().toLowerCase();

	const arcade: Shelved[] = ARCADE_LIST.map((entry) => ({
		kind: "arcade",
		id: entry.id,
		title: entry.title,
		emoji: entry.emoji,
		blurb: entry.blurb,
	}));

	const shown: Shelved[] = [
		// Arcade first under "All". There is one of them against forty-three of
		// the others, and a new thing at the bottom of a long list is a new thing
		// nobody finds.
		...arcade.filter((entry) => {
			if (needle.length > 0) return `${entry.title} ${entry.blurb}`.toLowerCase().includes(needle);
			return category === "all" || category === ARCADE_CATEGORY;
		}),
		...GAME_LIST.filter((game) => {
			// Searching looks past the shelf you are on: forty-three games is more
			// than a child wants to scroll, and a child who knows what they want
			// should not have to remember which shelf it lives on.
			if (needle.length > 0) {
				return `${game.title} ${game.blurb}`.toLowerCase().includes(needle);
			}
			return category === "all" || game.category === category;
		}).map(
			(game): Shelved => ({
				kind: "game",
				id: game.id,
				title: game.title,
				emoji: game.emoji,
				blurb: game.blurb,
			}),
		),
	];

	/*
	 * Starred games come first, always. In a long catalogue the two or three
	 * games a pair of friends actually play together were as far from a thumb
	 * as everything else, every single visit — the stars are how a child moves
	 * them within reach. Under a search the split would only reorder a handful
	 * of results, so there the two halves are shown as one list.
	 */
	const isStarred = (entry: Shelved) => starred.includes(favouriteKey(entry));
	const pinned = shown.filter(isStarred);
	const rest = shown.filter((entry) => !isStarred(entry));

	const toggleStar = (entry: Shelved) => {
		play("tap");
		setStarred(toggleFavourite(favouriteKey(entry)));
	};

	const pickEntry = (entry: Shelved) =>
		entry.kind === "arcade" ? onArcade(entry.id) : onPick(entry.id, { bestOf, turnSeconds });

	// The two shelves only appear once there is something to pin; with no
	// favourites the picker looks exactly as it always has.
	const grouped = pinned.length > 0 && needle.length === 0;

	return (
		<Card>
			<h2 className="mb-3 text-center text-2xl font-black">What shall we play?</h2>

			{/*
			 * The one text box in the whole app, and it never leaves the device:
			 * it filters a list that is already on the page. "No free text" is
			 * about what one child can send to another, and this cannot send
			 * anything to anybody.
			 */}
			{/*
			 * Forty-three is more than any child will scroll through, so the games
			 * near the bottom of the list were simply never being found. This
			 * prefers one you have not played.
			 */}
			<div className="mb-3 text-center">
				<Button
					tone="secondary"
					size="sm"
					onClick={() => onPick(surpriseGame(), { bestOf, turnSeconds })}
				>
					🎲 Surprise me
				</Button>
			</div>

			<div className="mb-4">
				<input
					type="search"
					value={search}
					onChange={(event) => setSearch(event.target.value)}
					placeholder="Search games…"
					aria-label="Search games"
					className="w-full rounded-2xl bg-white/80 px-4 py-3 text-lg shadow-inner outline-none placeholder:text-indigo-900/40 focus:ring-2 focus:ring-indigo-500 dark:bg-white/10 dark:placeholder:text-indigo-100/40"
				/>
			</div>

			<div className={`mb-5 flex flex-wrap justify-center gap-2 ${needle ? "hidden" : ""}`}>
				<CategoryChip
					label="All"
					emoji="⭐"
					active={category === "all"}
					onClick={() => setCategory("all")}
				/>
				<CategoryChip
					label="Arcade"
					emoji="🕹️"
					active={category === ARCADE_CATEGORY}
					onClick={() => setCategory(ARCADE_CATEGORY)}
				/>
				{stocked.map((name) => (
					<CategoryChip
						key={name}
						label={CATEGORY_LABEL[name].title}
						emoji={CATEGORY_LABEL[name].emoji}
						active={category === name}
						onClick={() => setCategory(name)}
					/>
				))}
			</div>

			{shown.length === 0 && (
				<p className="py-6 text-center text-lg font-semibold opacity-70">
					Nothing called that. Try a shorter word?
				</p>
			)}

			{/*
			 * No "Favourites" heading while searching: a search already puts what
			 * was asked for at the top, and a heading over two of five results is
			 * furniture. The starred ones still lead — `pinned` comes first.
			 */}
			{grouped && (
				<>
					<h3 className="mb-2 text-sm font-bold uppercase tracking-wide opacity-70">
						⭐ Favourites
					</h3>
					<Shelf entries={pinned} isStarred={isStarred} onStar={toggleStar} onPick={pickEntry} />
					{rest.length > 0 && (
						<h3 className="mt-5 mb-2 text-sm font-bold uppercase tracking-wide opacity-70">
							Everything else
						</h3>
					)}
				</>
			)}

			<Shelf
				entries={grouped ? rest : [...pinned, ...rest]}
				isStarred={isStarred}
				onStar={toggleStar}
				onPick={pickEntry}
			/>

			<MatchSettings
				bestOf={bestOf}
				turnSeconds={turnSeconds}
				onBestOf={setBestOf}
				onTurnSeconds={setTurnSeconds}
			/>
		</Card>
	);
}

/** One run of game cards. Three across on a tablet. Forty-odd games
    two-at-a-time is a 3,200px scroll on a screen with 400px of empty margin
    either side. */
function Shelf({
	entries,
	isStarred,
	onStar,
	onPick,
}: {
	entries: Shelved[];
	isStarred: (entry: Shelved) => boolean;
	onStar: (entry: Shelved) => void;
	onPick: (entry: Shelved) => void;
}) {
	if (entries.length === 0) return null;
	return (
		<ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
			{entries.map((entry) => {
				const starred = isStarred(entry);
				return (
					// Relative, because the star sits on the card but must not be
					// inside it: a button in a button is not a thing, and a mis-hit
					// star that also started the game would teach a child not to
					// touch the stars at all.
					<li key={`${entry.kind}:${entry.id}`} className="relative">
						<button
							type="button"
							onClick={() => onPick(entry)}
							className="flex w-full items-start gap-4 rounded-2xl bg-white/80 p-4 pr-12 text-left shadow-md transition hover:scale-[1.02] hover:bg-white dark:bg-white/10 dark:hover:bg-white/20"
						>
							<span className="text-4xl" aria-hidden="true">
								{entry.emoji}
							</span>
							<span>
								<span className="block text-xl font-bold">
									{entry.title}
									{/* Says up front that this one is not taking turns — the
									    settings below do not apply to it, and a child who
									    expects to go second would be waiting a while. */}
									{entry.kind === "arcade" && (
										<span className="ml-2 rounded-full bg-fuchsia-600 px-2 py-0.5 align-middle text-xs font-black uppercase tracking-wide text-white">
											Arcade
										</span>
									)}
								</span>
								<span className="block text-sm text-indigo-900/70 dark:text-indigo-100/70">
									{entry.blurb}
								</span>
							</span>
						</button>
						{/* A thumb-sized target in its own right — the corner of a card
						    is exactly where a small finger is least accurate. */}
						<button
							type="button"
							aria-pressed={starred}
							aria-label={
								starred
									? `Remove ${entry.title} from favourites`
									: `Add ${entry.title} to favourites`
							}
							onClick={(event) => {
								// Belt and braces: the star is a sibling of the card, not a
								// child, so nothing should bubble — but starring a game must
								// never also start it.
								event.stopPropagation();
								onStar(entry);
							}}
							className={`absolute top-1 right-1 rounded-2xl p-2 text-2xl transition hover:scale-110 ${
								starred ? "" : "opacity-30 grayscale hover:opacity-60"
							}`}
						>
							<span aria-hidden="true">⭐</span>
						</button>
					</li>
				);
			})}
		</ul>
	);
}

function CategoryChip({
	label,
	emoji,
	active,
	onClick,
}: {
	label: string;
	emoji: string;
	active: boolean;
	onClick: () => void;
}) {
	return (
		<button
			type="button"
			aria-pressed={active}
			onClick={onClick}
			className={`rounded-2xl px-4 py-2 font-bold shadow-md transition ${
				active ? "bg-indigo-600 text-white" : "bg-white/70 hover:bg-white dark:bg-white/10"
			}`}
		>
			<span aria-hidden="true">{emoji}</span> {label}
		</button>
	);
}
